/** 
** * Represents a rich grid component that supports pagination, and sorting 

* @param {Column[]]} columns - a list of column objects with rendering logic and styles for each column. The length determines the number of columns of the grid 
	* @typedef {Object} Column 
	* @property {string} field - field for sorting the rich grid. If a column does not have a field, it is not sortable 
	* @property {string|React.Component} header - heading of the column. If not set, heading will be empty 
	* @property {RenderLogic} renderLogic - rendering logic given row data. If not set, column will be empty 
	* @property {Object} containerStyles - any styling of column item's container can be passed via this prop 

* @param {Object[]]} data - a list object, the structure of the objects does not matter 
* @param {function} extractRowKey - callback function called on each row ro get unique key

* @param {array} topHeaderColumns - To render additional table header. array of object with header property. Pass an empty object to skip the column
	example: [
				{ header: <CheckboxWLabel label={"Select All"} weight={LABEL_WEIGHT[700]} /> },
				{ header: <CheckboxWLabel label={"State Filing"} weight={LABEL_WEIGHT[700]} /> },
				{},
			]

* @param {SortProps} sortProps - props needed for the grid to be responsive to sort actions can be passed via this prop 
	* @typedef {Object} SortProps 
	* @property {string} field - field to sort on, one of the headers 
	* @property {string} dir - direction of sort, should be from values in SORT_DIRECTIONS enum 
	* @property {onSortChange} onChange - callback function to be called on sort change. If not passed grid will not be responsive to sort changes

* @param {PaginationProps} paginationProps - properties to be passed to Pagination element. By default, is false and the grid will show no paginationhable
	* @typedef {Object} PaginationProps 
	* @property {number} skip - a number showing elements skipped of total elements 
	* @property {number} take - a number showing total elements per page 
	* @property {number} total - a number showing total data (usually total count in DB) 
	* @property {object[]]} takeOptions - a list of number to select take from
	Example: [{ label: "10 Rows", value: 10 }, { label: "20 Rows", value: 20 }]

* @property {onChange} onChangeTakeCb - callback function when takeOption is changed 
	* @property {OnChange} onChange - callback function to be called when skip or take is changed 
	* @property {FlexEndRenderLogic} [flexEndRenderLogic] - component returned by this function will be rendered to the right of pagination render

* note - the grid itself does not sort the data. It shows on the UI and helps with callbacks on changing sort 	 
*/

import React, { useEffect, useState } from "react";
import { SORT_DIRECTIONS } from "./constants";
import { Row, Col, Container, Input, Table } from 'reactstrap';
import Pagination from "./Pagination";
import Checkbox from "../checkbox/basecheckbox/Checkbox";
import styles from './RichGrid.styles';
import "./directcss/RichGrid.scss"
import { VEC_ICON_NAME } from "../icons/constants";
import Icons from "../icons/Icons";
import SelectAllConfirmationPopup from "../popup/selectallconfirmationpopup/SelectAllConfirmationPopup";
import TxTooltipWIcon from "../tooltip/txtooltipwicon/TxTooltipWIcon";

export default function RichGrid({
	columns = [],
	data = [],
	selectable = null,
	selectedRows = [],
	onSelectionChangeCallBack = (rows) => { },
	onHeaderSelectionChangeCallBack = (rows) => { },
	showHeaderSelectionCheckbox = true,
	extractRowKey,
	sortProps = {
		field: null,
		dir: SORT_DIRECTIONS.NONE,
		onChange: () => { },
	},
	paginationProps = null,
	rowStyleFn = null,
	sortFields,
	onSortChange,
	checkboxProps = { checkboxContainerStyle: {} },
	groupBy = null,
	groupByRowProps = {},
	setSelectTotalRecordsCb = () => { },
	topHeaderColumns = [],
	showSelectAllRecordsWarningPopup = false,
}) {

	const [headerCheckbox, setHeaderCheckbox] = useState(false);
	const [expandedGroups, setExpandedGroups] = useState([]);
	const [showSelectAllWarningPopup, setShowSelectAllWarningPopup] = useState(false);

	useEffect(() => {
		selectedRows.length ? setHeaderCheckbox(true) : setHeaderCheckbox(false)
	}, [selectedRows]);

	const getChangedSortPropsOnClick = (changedSortField) => {
		if (sortProps.field == changedSortField) {
			if (sortProps.dir == SORT_DIRECTIONS.ASC) {
				return { field: changedSortField, dir: SORT_DIRECTIONS.DESC }
			}
			else if (sortProps.dir == SORT_DIRECTIONS.DESC) {
				return { field: null, dir: SORT_DIRECTIONS.ASC }
			}
			else {
				return { field: changedSortField, dir: SORT_DIRECTIONS.ASC }
			}
		}
		else {
			return { field: changedSortField, dir: SORT_DIRECTIONS.ASC }
		}
	}

	const toggleGroup = (groupValue) => {
		const isGroupExpanded = expandedGroups.includes(groupValue);
		if (isGroupExpanded) {
			setExpandedGroups(expandedGroups.filter((value) => value !== groupValue));
		} else {
			setExpandedGroups([...expandedGroups, groupValue]);
		}
	};

	useEffect(() => {
		const groupValues = data.map((row) => row[groupBy]);
		setExpandedGroups([...new Set(groupValues)]);
	}, [data, groupBy]);

	const renderGroupHeader = (dataRow, isGroupExpanded, groupHeaderCircleColor = null) => {
		let groupValue = dataRow?.groupValue;
		const iconClassName = isGroupExpanded ? "fa-caret-down" : "fa-caret-right";

		const isGroupHeaderSelected = data
			.filter((row) => row[groupBy] === groupValue)
			.every((row) => selectedRows.includes(row));

		return (
			<tr key={groupValue}>
				<th style={styles.groupHeaderRow} colSpan={selectable ? columns.length + 1 : columns.length}>
					<div style={styles.groupHeaderContent}>
						<span style={styles.toggleGroupIcon} onClick={() => toggleGroup(groupValue)}>
							<Icons familyName="fas" iconName={iconClassName} />
						</span>
						<span style={{ ...styles.flagCircle, ...styles[groupHeaderCircleColor ?? ""] }}></span>
						<Checkbox
							checked={isGroupHeaderSelected}
							onChangeCb={(status) => {
								const isChecked = status.target.checked;
								const groupRows = data.filter((row) => row[groupBy] === groupValue);
								const updatedSelectedRows = isChecked
									? [...selectedRows, ...groupRows]
									: selectedRows.filter((row) => !groupRows.includes(row));

								onSelectionChangeCallBack(updatedSelectedRows);
								if (!isChecked) {
									setSelectTotalRecordsCb(isChecked);
								}
							}}
						/>
						<span onClick={() => groupByRowProps?.onLabelClickCb(dataRow)} style={groupByRowProps?.labelStyles ?? {}}>{(groupValue && groupValue !== "") ? groupValue : "NONAME"}</span>
						{
							dataRow?.errorMessage && <span>
								<TxTooltipWIcon
									id={`payer_error_${dataRow?.payerTin}`}
									text={dataRow?.errorMessage}
									iconName={VEC_ICON_NAME.ERROR_ICON}
									customTooltipstyles={styles.errorTooltip}
								/>
							</span>
						}
						
					</div>
				</th>
			</tr>
		);
	};

	let gridColumns = [...columns];
	if (selectable) {
		gridColumns.unshift({
			field: "checkbox",
			header: ( (selectable && showHeaderSelectionCheckbox) ?
				<Checkbox
					checked={(headerCheckbox || data.length === selectedRows.length)}
					iconName={VEC_ICON_NAME.CHECKBOX_DASH_ICON}
					onChangeCb={(status) => {
						const isChecked = status.target.checked;
						onHeaderSelectionChangeCallBack(isChecked ? data : []);
						if(isChecked && showSelectAllRecordsWarningPopup){
							setShowSelectAllWarningPopup(true);
						}
						setHeaderCheckbox(isChecked);
						if (!isChecked) {
							setSelectTotalRecordsCb(isChecked);
						}
					}}
				/> : null
			),
			renderLogic: (row, indx) => (
				<Checkbox
					onChangeCb={(status) => {
						const isChecked = status.target.checked;
						let newSelectedRows;
						if (!isChecked) {
							newSelectedRows = [...selectedRows].filter((selectedRow) => extractRowKey(row) !== extractRowKey(selectedRow));
						} else {
							newSelectedRows = [...selectedRows];
							newSelectedRows.push(row);
						}
						onSelectionChangeCallBack(newSelectedRows, indx);
						if (!isChecked) {
							setSelectTotalRecordsCb(isChecked);
						}
					}}
					checked={selectedRows.find((selectedRow) => extractRowKey(row) === extractRowKey(selectedRow))}
					containerStyles={checkboxProps.checkboxContainerStyle}
				/>
			),
		});
	}

	let groupedData = [...data];
	if (groupBy) {
		groupedData = [];
		const groups = {};
		data.forEach((row) => {
			const groupValue = row[groupBy];
			if (groupValue in groups) {
				groups[groupValue].push(row);
			} else {
				groups[groupValue] = [row];
			}
		});

		Object.entries(groups).forEach(([groupValue, rows]) => {
			const groupRows = rows.filter((row) => !row.isGroupHeader);
			const hasRed = groupRows.some((row) => row.payerColor === "red");
			const hasYellow = !hasRed && groupRows.some((row) => row.payerColor === "yellow");

			groupedData.push({ isGroupHeader: true, groupValue, payerTin: rows[0]?.PayerTIN ?? null, FormId: rows[0]?.FormId, groupHeaderCircleColor: hasRed ? "red" : hasYellow ? "yellow" : "green", errorMessage: rows[0]?.PayerErrors });
			if (expandedGroups.includes(groupValue)) {
				groupedData.push(...rows);
			}
		});
	}

	return <div style={styles.container}>
		<div style={styles.tableContainer}>
			<Table size={'sm'} bordered hover responsive cssModule={styles.overflowClip}>
				<thead>

					{topHeaderColumns.length ? <tr style={styles.headerRowContainer}>
						{topHeaderColumns.map(column =>

							<th style={{ ...styles.commonRowContainer, ...(column?.field === 'checkbox' ? styles.checkboxColumnStyles : {}) }} >
								<div>
									<div>
										{column.header}
									</div>
								</div>
							</th>

						)}
					</tr> : null}

					<tr style={styles.headerRowContainer}>
						{
							gridColumns.map(column => {
								const isSortable = (!!column.field);
								const isSorted = isSortable && sortProps.field === column.field && sortProps.dir !== SORT_DIRECTIONS.NONE;
								return <th style={{ ...styles.commonRowContainer, ...((column?.field === 'checkbox' && groupBy) ? styles.checkboxColumnStyles : {}) }} >
									<div
										onClick={() => column.field && sortProps.onChange(getChangedSortPropsOnClick(column.field))}
									>
										<div
											style={isSortable ? styles.headerCellContainerSortable : styles.headerCellContainer}>
											{column.header}
											{isSortable &&
												(
													<div style={styles.sortIcon}>
														{isSorted ?
															(sortProps.dir === SORT_DIRECTIONS.DESC ? <Icons fontFamily="fas" iconName="fa-arrow-up" /> : <Icons fontFamily="fas" iconName="fa-arrow-down" />) :
															<Icons fontFamily="fas" iconName="fa-arrow-alt" />
														}
													</div>
												)
											}
										</div>
									</div>
								</th>
							}
							)
						}
					</tr>
				</thead>
				<tbody>
					{groupedData.map((row, idx) => {
						if (row.isGroupHeader) {
							const isGroupExpanded = expandedGroups.includes(row.groupValue);
							return renderGroupHeader(row, isGroupExpanded, row?.groupHeaderCircleColor ?? false);
						} else {
							const rowStyle = rowStyleFn ? rowStyleFn(row, idx) : {};
							return (
								<tr style={{ ...styles.tableBodyRowContainer, ...rowStyle }}>
									{gridColumns.map((column) => (
										<td style={{ ...styles.commonRowContainer, ...(column.cellStyleFn ? column.cellStyleFn(row) : {}) }}>
											{column.renderLogic(row, idx)}
										</td>
									))}
								</tr>
							);
						}
					})}
				</tbody>
			</Table>
		</div>
		{data.length == 0 && <p style={styles.dataNotFound}>No records were found</p>}
		{paginationProps &&
			<div style={styles.paginationParentContainer}>
				<Pagination
					{...paginationProps}
				/>
				<br />
			</div>
		}
		{
			showSelectAllWarningPopup && <SelectAllConfirmationPopup
				isOpen={showSelectAllWarningPopup}
				onConfirmationCb={() => setShowSelectAllWarningPopup(false)}
				onCancelCb={() => setShowSelectAllWarningPopup(false)}
			/>
		}

	</div>
}
